import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { useDashPayments, useFinanceSummary } from '../api'
import type { WidgetBodyProps } from '../types'
import { WidgetItems, WidgetState } from './WidgetItems'

export function PaymentsWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useDashPayments()
  const items = (q.data ?? []).map((p) => ({
    id: p.id,
    title: p.title,
    subtitle: p.party,
    meta: fmtDate(p.due_on),
    trailing: (
      <span className="flex flex-col items-end gap-0.5">
        <span className="text-[13px] font-semibold tabular-nums">{fmtMoney(p.amount, p.currency)}</span>
        <Badge tone={p.direction === 'in' ? 'success' : 'danger'} className="px-1.5 py-0 text-[10px]">
          {t(`dashboard.widgets.payments.${p.direction}`)}
        </Badge>
      </span>
    ),
  }))

  return (
    <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.widgets.payments.empty')}>
      <WidgetItems items={items} view={config.view} density={config.density} />
    </WidgetState>
  )
}

const TILES = [
  { key: 'income', className: 'text-success' },
  { key: 'expense', className: 'text-destructive' },
  { key: 'net', className: 'text-primary' },
  { key: 'cash', className: 'text-foreground' },
] as const

export function FinanceSummaryWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useFinanceSummary()
  const data = q.data
  const compact = config.density === 'compact'

  return (
    <WidgetState query={q} empty={!data} emptyText={t('dashboard.widgets.financeSummary.empty')}>
      {data ? (
        <div className="flex h-full min-h-0 flex-col gap-3">
          <div className={cn('grid grid-cols-2', compact ? 'gap-1.5' : 'gap-2')}>
            {TILES.map((tile) => (
              <div key={tile.key} className={cn('min-w-0 rounded-xl border bg-muted/30 dark:bg-muted/15', compact ? 'p-2' : 'p-3')}>
                <p className="truncate text-[11px] font-medium text-muted-foreground">{t(`dashboard.widgets.financeSummary.${tile.key}`)}</p>
                <p className={cn('truncate text-sm font-semibold tabular-nums', tile.className)}>{fmtMoney(data[tile.key], data.currency)}</p>
              </div>
            ))}
          </div>
          {config.h >= 3 ? (
            <div className="min-h-28 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-[11px]" />
                  <Tooltip />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" name={t('dashboard.widgets.financeSummary.incomeK')} fill="var(--chart-3)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expense" name={t('dashboard.widgets.financeSummary.expenseK')} fill="var(--chart-5)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>
      ) : null}
    </WidgetState>
  )
}
